import ScButton from "@/src/components/ui-kit/button";
import { useShareWithPoints } from "@/src/hooks/features/posts/use-share-with-points";
import { useTheme } from "@/src/styles/theme-provider";
import {
  resourceUrl,
  type DeepLinkResource,
} from "@/src/utils/deep-link.utils";
import * as Clipboard from "expo-clipboard";
import { Copy, CopySuccess } from "iconsax-react-nativejs";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  Linking,
  Modal,
  Pressable,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

const SocialIconFromUrl = ({ domain }: { domain: string }) => {
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  return <Image source={{ uri: faviconUrl }} className="w-8 h-8 " />;
};

const WhatsAppIcon = () => <SocialIconFromUrl domain="wa.me" />;
const MessengerIcon = () => <SocialIconFromUrl domain="messenger.com" />;
const TeamsIcon = () => <SocialIconFromUrl domain="teams.microsoft.com" />;
const TelegramIcon = () => <SocialIconFromUrl domain="telegram.org" />;

type ShareOption = Readonly<{
  label: string;
  Icon: React.FC;
  onPress: (url: string, title: string) => void;
}>;

export type ShareModalProps = Readonly<{
  visible: boolean;
  onClose: () => void;
  resourceId: string;
  /**
   * Which kind of thing is being shared. This used to be a router pathname
   * (`"/(tabs)/news/news-detail"`), which produced links no browser could
   * resolve — see `deep-link.utils` for the two rules that broke.
   */
  resource: DeepLinkResource;
  title?: string;
  onShare?: () => void;
}>;

const ShareModal = memo(function ShareModal({
  visible,
  onClose,
  resourceId,
  resource,
  title,
  onShare,
}: ShareModalProps) {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const [copied, setCopied] = useState(false);

  // ─── Points ───────────────────────────────────────────────────────────────
  // Post shares are reported to the leaderboard (credited once per user and post on the server).
  const { handleSharedSuccessfully } = useShareWithPoints();
  const isPostShare = resource === "post";

  const shareUrl = resourceUrl(resource, resourceId);

  const SHARE_OPTIONS: ShareOption[] = useMemo(
    () => [
      {
        label: t("LABEL_WHATSAPP"),
        Icon: WhatsAppIcon,
        onPress: (url, title) =>
          Linking.openURL(
            `https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`,
          ),
      },
      {
        label: t("LABEL_MESSENGER"),
        Icon: MessengerIcon,
        onPress: (url) => {
          const enc = encodeURIComponent(url);
          Linking.openURL(`fb-messenger://share?link=${enc}`).catch(() =>
            Linking.openURL(
              `https://www.facebook.com/dialog/send?link=${enc}&redirect_uri=${enc}`,
            ),
          );
        },
      },
      {
        label: t("LABEL_TEAMS"),
        Icon: TeamsIcon,
        onPress: (url, title) =>
          Linking.openURL(
            `https://teams.microsoft.com/share?href=${encodeURIComponent(url)}&msgText=${encodeURIComponent(title)}`,
          ),
      },
      {
        label: t("LABEL_TELEGRAM"),
        Icon: TelegramIcon,
        onPress: (url, title) =>
          Linking.openURL(
            `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
          ),
      },
    ],
    [t],
  );

  const handleCopy = useCallback(async () => {
    await Clipboard.setStringAsync(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onShare?.();
    if (isPostShare) handleSharedSuccessfully(resourceId);
  }, [shareUrl, onShare, isPostShare, handleSharedSuccessfully, resourceId]);

  const handleOptionPress = useCallback(
    (option: ShareOption) => {
      option.onPress(shareUrl, title ?? "");
      onShare?.();
      if (isPostShare) handleSharedSuccessfully(resourceId);
      onClose();
    },
    [shareUrl, title, onClose, onShare, isPostShare, handleSharedSuccessfully, resourceId],
  );

  const rows: ShareOption[][] = useMemo(() => {
    const result: ShareOption[][] = [];
    for (let i = 0; i < SHARE_OPTIONS.length; i += 4) {
      result.push(SHARE_OPTIONS.slice(i, i + 4));
    }
    return result;
  }, [SHARE_OPTIONS]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        style={varStyle}
        className="flex-1 items-center justify-center bg-shadow-dark px-6"
        onPress={onClose}
      >
        <Pressable
          className="w-full max-w-md bg-ash rounded-2xl pt-6 pb-5 px-5"
          onPress={(e) => e.stopPropagation()}
        >
          <Text className="text-[15px] font-bold text-center mb-4 text-onyx">
            {t("LABEL_SHARE")}
          </Text>

          {rows.map((row, rowIndex) => (
            <View key={rowIndex} className="flex-row justify-around mb-5">
              {row.map((option) => (
                <TouchableOpacity
                  key={option.label}
                  onPress={() => handleOptionPress(option)}
                  activeOpacity={0.7}
                  className="items-center py-2 w-16 hover:bg-ash-3 hover:rounded-xl"
                >
                  <option.Icon />
                  <Text
                    className="text-slate text-center mt-1.5 font-medium"
                    numberOfLines={1}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}

              {row.length < 4 &&
                Array.from({ length: 4 - row.length }).map((_, idx) => (
                  <View key={`empty-${idx}`} className="w-16" />
                ))}
            </View>
          ))}

          <View className="h-px bg-ash-3 mb-3.5" />

          <View className="mb-4 flex-row items-center bg-obsidian rounded-xl border border-ash-3 px-2.5 py-2 gap-2">
            <Text numberOfLines={1} className="flex-1 text-xs text-slate">
              {shareUrl}
            </Text>
            <Pressable
              onPress={handleCopy}
              className="flex-row items-center gap-2 p-2 rounded-lg bg-ash-3 hover:bg-primary-2"
            >
              {copied ? (
                <CopySuccess
                  size={18}
                  color={themeVars["--color-primary-default"]}
                />
              ) : (
                <Copy size={18} color={themeVars["--color-primary-default"]} />
              )}
            </Pressable>
          </View>

          <ScButton
            text="BTN_CANCEL"
            onPress={onClose}
            mode="outlined"
            className="mt-2"
          />
        </Pressable>
      </Pressable>
    </Modal>
  );
});

export default ShareModal;
